import { CreateDocRequestV1, CreatedDocumentV1, DocIdParams, DocMetaV1, DocTitlesRequestV1, DocTitlesResponseV1, DocumentV1, MoveBlocksRequestV1, MoveBlocksResponseV1, PatchMetaRequestV1, PatchMetaResponseV1, PutBodyRequestV1, PutBodyResponseV1, } from '../dto/documents.js';
import { defineRoute } from '../route/define-route.js';
import { LARGE_BODY_BYTES } from '../route/types.js';
import { HEADER_FROM_2L_S1 } from './change-batch-senders.js';
import { EXAMPLE_IDS } from './example-ids.js';
import { EXAMPLE_CREATE_BODY, EXAMPLE_CREATED_DOC, EXAMPLE_DOC, EXAMPLE_DOC_ID, EXAMPLE_DOC_META, EXAMPLE_MOVE_TOKEN, EXAMPLE_MOVED_PAGE_META, EXAMPLE_SAVE_BODY, EXAMPLE_TITLES, } from './examples-docs.js';
/**
 * The skeleton and editor document routes (1.0b deliverable 7; baseline section 4 "Save
 * pipeline"), frozen: the shapes are fixed, and each route is implemented by its owner. A frozen
 * route joins the server's sweeps once its owner has merged (`implementedRoutes`).
 */
/**
 * Create a document. A reference type key answers 403 `reference_read_only`; an unknown or
 * archived one, the `session` type (a session is made by `sessions.create`, with its
 * `session_meta`; DECISIONS 139 1.T item 4), or a body `validateBody` refuses, 422 `invalid_body`;
 * a schema version the server cannot read 422 `unsupported_schema_version`. Bodies up to
 * `LARGE_BODY_BYTES`.
 */
export const docsCreate = defineRoute({
    id: 'docs.create',
    method: 'POST',
    path: '/api/docs',
    body: CreateDocRequestV1,
    maxBodyBytes: LARGE_BODY_BYTES,
    response: { kind: 'json', schema: CreatedDocumentV1, status: 201 },
    errors: { 403: ['reference_read_only'], 422: ['invalid_body', 'unsupported_schema_version'] },
    auth: 'session',
    smoke: 'deny',
    reference: 'none',
    status: 'frozen',
    owner: '1.1',
    since: 'editor-alpha',
    examples: {
        request: {
            body: {
                typeKey: 'quest',
                title: 'The Drowned Bell',
                body: EXAMPLE_CREATE_BODY,
                bodySchemaVersion: 1,
            },
        },
        response: EXAMPLE_CREATED_DOC,
    },
});
/** A whole document: the editor's clean reload and the conflict view (baseline step 8). */
export const docsGet = defineRoute({
    id: 'docs.get',
    method: 'GET',
    path: '/api/docs/:id',
    params: DocIdParams,
    response: { kind: 'json', schema: DocumentV1 },
    auth: 'session',
    smoke: 'read',
    reference: 'read',
    docIds: { owner: ['params.id'] },
    status: 'frozen',
    owner: '1.1',
    since: 'editor-alpha',
    examples: { request: { params: { id: EXAMPLE_DOC_ID } }, response: EXAMPLE_DOC },
});
/**
 * A save. 409 `version_conflict` when `baseBodyVersion` is stale (details
 * `VersionConflictDetailsV1`), 409 `document_trashed`; 422 `invalid_body` and
 * `unsupported_schema_version`. A block moved in from another document names it in `moves`, so
 * `body.moves[].fromDocId` is an owner pointer: nothing moves out of a reference document.
 */
export const docsPutBody = defineRoute({
    id: 'docs.putBody',
    method: 'PUT',
    path: '/api/docs/:id/body',
    params: DocIdParams,
    body: PutBodyRequestV1,
    maxBodyBytes: LARGE_BODY_BYTES,
    response: { kind: 'json', schema: PutBodyResponseV1 },
    errors: {
        409: ['version_conflict', 'document_trashed'],
        422: ['invalid_body', 'unsupported_schema_version'],
    },
    auth: 'session',
    smoke: 'deny',
    reference: 'deny',
    docIds: { owner: ['params.id', 'body.moves[].fromDocId'] },
    status: 'frozen',
    owner: '1.1',
    since: 'editor-alpha',
    examples: {
        request: {
            params: { id: EXAMPLE_IDS.questSunkenChapel.id },
            body: {
                baseBodyVersion: 1,
                clientSeq: 1,
                body: EXAMPLE_SAVE_BODY,
                moves: [
                    {
                        id: EXAMPLE_IDS.blockBellsMoved.id,
                        fromDocId: EXAMPLE_IDS.questBellsBeneathTheMarsh.id,
                        moveToken: EXAMPLE_MOVE_TOKEN,
                    },
                ],
                bodySchemaVersion: 1,
            },
        },
        response: { bodyVersion: 2, clientSeq: 1, patches: [], warnings: [] },
        consumes: true,
    },
});
/**
 * One metadata field; bumps only `metaVersion`. A stale `baseMetaVersion` answers 409
 * `version_conflict`; a trashed document 409 `document_trashed`; a blank title, an unknown cover
 * asset or an unknown or inapplicable field 422 `invalid_body`.
 */
export const docsPatchMeta = defineRoute({
    id: 'docs.patchMeta',
    method: 'PATCH',
    path: '/api/docs/:id/meta',
    params: DocIdParams,
    body: PatchMetaRequestV1,
    response: { kind: 'json', schema: PatchMetaResponseV1 },
    errors: { 409: ['version_conflict', 'document_trashed'], 422: ['invalid_body'] },
    auth: 'session',
    smoke: 'deny',
    reference: 'deny',
    docIds: { owner: ['params.id'] },
    status: 'frozen',
    owner: '1.1',
    since: 'editor-alpha',
    changeBatch: true,
    changeBatchFrom: HEADER_FROM_2L_S1,
    examples: {
        request: {
            params: { id: EXAMPLE_DOC_ID },
            body: { field: 'title', value: 'The Missing Lamplighters' },
        },
        response: { metaVersion: 2 },
    },
});
/** A document's metadata with its type's capabilities and its home. */
export const docsMeta = defineRoute({
    id: 'docs.meta',
    method: 'GET',
    path: '/api/docs/:id/meta',
    params: DocIdParams,
    response: { kind: 'json', schema: DocMetaV1 },
    auth: 'session',
    smoke: 'read',
    reference: 'read',
    docIds: { owner: ['params.id'] },
    status: 'frozen',
    owner: '1.1',
    since: 'editor-alpha',
    examples: { request: { params: { id: EXAMPLE_DOC_ID } }, response: EXAMPLE_DOC_META },
});
/**
 * The titles of up to 500 documents, one per distinct id in the order first asked: live,
 * trashed, merged or missing. A POST because the ids do not fit a query; it writes nothing, so a
 * smoke session may call it.
 */
export const docsTitles = defineRoute({
    id: 'docs.titles',
    method: 'POST',
    path: '/api/docs/titles',
    body: DocTitlesRequestV1,
    response: { kind: 'json', schema: DocTitlesResponseV1 },
    auth: 'session',
    smoke: 'read',
    reference: 'read',
    docIds: { owner: ['body.ids[]'] },
    status: 'frozen',
    owner: '1.1',
    since: 'editor-alpha',
    examples: {
        request: {
            body: {
                ids: [
                    EXAMPLE_IDS.questMissingLamplighters.id,
                    EXAMPLE_IDS.noteTornLedger.id,
                    EXAMPLE_IDS.noteLamplighterNotes.id,
                    EXAMPLE_IDS.docMissing.id,
                ],
            },
        },
        response: { titles: EXAMPLE_TITLES },
    },
});
/**
 * Turn into page, Move to and Split at heading (checklist item 4b, behind `page_split`): blocks
 * leave the source for existing documents or new child pages, keeping block ids, secret ids,
 * mentions and registered references.
 */
export const docsMoveBlocks = defineRoute({
    id: 'docs.moveBlocks',
    method: 'POST',
    path: '/api/docs/:id/move-blocks',
    params: DocIdParams,
    body: MoveBlocksRequestV1,
    response: { kind: 'json', schema: MoveBlocksResponseV1 },
    errors: { 409: ['version_conflict', 'document_trashed'], 422: ['invalid_body'] },
    auth: 'session',
    smoke: 'deny',
    reference: 'deny',
    docIds: { owner: ['params.id', 'body.destinations[].docId'] },
    status: 'frozen',
    owner: '1.A',
    stage: '2',
    since: 'exit-1a',
    examples: {
        request: {
            params: { id: EXAMPLE_IDS.loreHistoryOfVesk.id },
            body: {
                baseBodyVersion: 1,
                destinations: [
                    {
                        blockIds: [
                            EXAMPLE_IDS.blockHistoryFloodHeading.id,
                            EXAMPLE_IDS.blockHistoryFloodText.id,
                        ],
                        title: 'The Flood',
                        typeKey: 'lore',
                    },
                    {
                        blockIds: [EXAMPLE_IDS.blockHistoryLanternQuarter.id],
                        docId: EXAMPLE_IDS.locationLanternQuarter.id,
                    },
                ],
            },
        },
        response: {
            source: { bodyVersion: 2, clientSeq: 0, patches: [], warnings: [] },
            created: [EXAMPLE_MOVED_PAGE_META],
        },
        consumes: true,
    },
});
//# sourceMappingURL=docs.js.map